import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Scissors, 
  Palette, 
  Sun, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  Euro,
  Calendar,
  User,
  Phone,
  Sparkles,
  ChevronRight,
  History,
  CalendarPlus,
  ArrowLeft
} from "lucide-react";

/**
 * Componente Storico Cliente
 * Mostra lo storico trattamenti con alert tempistiche
 */
export default function StoricoCliente({ 
  clienteId, 
  clienteNome,
  open, 
  onOpenChange,
  token,
  showBackToAgenda = false,
  onBackToAgenda = null
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [storico, setStorico] = useState(null);
  const [activeTab, setActiveTab] = useState("riepilogo");

  useEffect(() => {
    if (open && clienteId) {
      fetchStorico();
    }
  }, [open, clienteId]);

  const fetchStorico = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/clienti/${clienteId}/storico-trattamenti`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStorico(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getPrioritaColor = (priorita) => {
    switch (priorita) {
      case "alta": return "bg-red-100 text-red-700 border-red-200";
      case "media": return "bg-amber-100 text-amber-700 border-amber-200";
      case "bassa": return "bg-green-100 text-green-700 border-green-200";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const formatData = (data) => {
    if (!data) return "-";
    try {
      const d = new Date(data);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return data;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 pb-2 shrink-0 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <History className="w-5 h-5 text-purple-600" />
              Storico Cliente
            </DialogTitle>
            <div className="flex items-center gap-2">
              {/* Pulsante torna all'agenda (se arriva da lì) */}
              {showBackToAgenda && onBackToAgenda && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onOpenChange(false);
                    onBackToAgenda();
                  }}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50 h-8"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Agenda
                </Button>
              )}
            </div>
          </div>
          {storico?.cliente && (
            <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
              <User className="w-4 h-4" />
              <span className="font-medium">{storico.cliente.nome} {storico.cliente.cognome}</span>
              {storico.cliente.telefono && (
                <>
                  <span>•</span>
                  <Phone className="w-3 h-3" />
                  <span>{storico.cliente.telefono}</span>
                </>
              )}
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : storico ? (
          <div className="flex-1 overflow-y-auto">
            {/* Alert Tempistiche - Sempre visibili in alto */}
            {storico.alert && storico.alert.length > 0 && (
              <div className="p-3 bg-gradient-to-r from-amber-50 to-red-50 border-b space-y-2">
                <p className="text-xs font-medium text-slate-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  TRATTAMENTI DA PROPORRE
                </p>
                {storico.alert.map((alert) => (
                  <div 
                    key={`alert-${alert.titolo}-${alert.giorni}`}
                    className={`p-2 rounded-lg border ${getPrioritaColor(alert.priorita)}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm flex items-center gap-1">
                        <span>{alert.icona}</span>
                        {alert.titolo}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {alert.giorni}gg / {alert.soglia}gg
                      </Badge>
                    </div>
                    <p className="text-xs mt-1 opacity-80">{alert.messaggio}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
                <TabsTrigger 
                  value="riepilogo" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent py-2 px-4"
                >
                  Riepilogo
                </TabsTrigger>
                <TabsTrigger 
                  value="colore"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent py-2 px-4"
                >
                  🎨 Colore
                </TabsTrigger>
                <TabsTrigger 
                  value="taglio"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent py-2 px-4"
                >
                  ✂️ Taglio
                </TabsTrigger>
                <TabsTrigger 
                  value="schiaritura"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent py-2 px-4"
                >
                  ☀️ Schiariture
                </TabsTrigger>
              </TabsList>

              {/* Tab Riepilogo */}
              <TabsContent value="riepilogo" className="p-4 space-y-4 mt-0">
                {/* Statistiche */}
                <div className="grid grid-cols-3 gap-2">
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-3 text-center">
                      <Calendar className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                      <p className="text-xl font-bold text-purple-700">{storico.riepilogo.totale_visite}</p>
                      <p className="text-[10px] text-purple-600">Visite</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-3 text-center">
                      <Euro className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                      <p className="text-xl font-bold text-emerald-700">€{storico.riepilogo.totale_speso}</p>
                      <p className="text-[10px] text-emerald-600">Totale</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-3 text-center">
                      <TrendingUp className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                      <p className="text-xl font-bold text-blue-700">€{storico.riepilogo.fiche_media}</p>
                      <p className="text-[10px] text-blue-600">Media</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Tempistiche Rapide */}
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      TEMPISTICHE
                    </p>
                    <div className="space-y-2">
                      {/* Colore */}
                      <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm flex items-center gap-2">
                          <Palette className="w-4 h-4 text-pink-500" />
                          Colore
                          <span className="text-[10px] text-slate-400">(timer {storico.tempistiche.colore.soglia_giorni}gg)</span>
                        </span>
                        <div className="text-right">
                          {storico.tempistiche.colore.giorni_da_ultimo !== null ? (
                            <span className={`text-sm font-medium ${storico.tempistiche.colore.giorni_da_ultimo >= storico.tempistiche.colore.soglia_giorni ? 'text-red-600 font-bold' : storico.tempistiche.colore.giorni_da_ultimo >= (storico.tempistiche.colore.soglia_giorni - 10) ? 'text-amber-600' : 'text-slate-600'}`}>
                              {storico.tempistiche.colore.giorni_da_ultimo} giorni fa
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Mai fatto</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Taglio */}
                      <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm flex items-center gap-2">
                          <Scissors className="w-4 h-4 text-blue-500" />
                          Taglio
                          <span className="text-[10px] text-slate-400">(timer {storico.tempistiche.taglio.soglia_giorni}gg)</span>
                        </span>
                        <div className="text-right">
                          {storico.tempistiche.taglio.giorni_da_ultimo !== null ? (
                            <span className={`text-sm font-medium ${storico.tempistiche.taglio.giorni_da_ultimo >= storico.tempistiche.taglio.soglia_giorni ? 'text-red-600 font-bold' : storico.tempistiche.taglio.giorni_da_ultimo >= (storico.tempistiche.taglio.soglia_giorni - 10) ? 'text-amber-600' : 'text-slate-600'}`}>
                              {storico.tempistiche.taglio.giorni_da_ultimo} giorni fa
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Mai fatto</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Schiaritura */}
                      <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm flex items-center gap-2">
                          <Sun className="w-4 h-4 text-amber-500" />
                          Schiaritura
                          <span className="text-[10px] text-slate-400">(timer {storico.tempistiche.schiaritura.soglia_giorni}gg)</span>
                        </span>
                        <div className="text-right">
                          {storico.tempistiche.schiaritura.giorni_da_ultimo !== null ? (
                            <span className={`text-sm font-medium ${storico.tempistiche.schiaritura.giorni_da_ultimo >= storico.tempistiche.schiaritura.soglia_giorni ? 'text-red-600 font-bold' : storico.tempistiche.schiaritura.giorni_da_ultimo >= (storico.tempistiche.schiaritura.soglia_giorni - 30) ? 'text-amber-600' : 'text-slate-600'}`}>
                              {storico.tempistiche.schiaritura.giorni_da_ultimo} giorni fa
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Mai fatto</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Suggerimenti Marketing */}
                {storico.suggerimenti_marketing && storico.suggerimenti_marketing.length > 0 && (
                  <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                    <CardContent className="p-3">
                      <p className="text-xs font-medium text-purple-600 mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        SUGGERIMENTI
                      </p>
                      <div className="space-y-1">
                        {storico.suggerimenti_marketing.map((sug) => (
                          <p key={`sug-${sug.messaggio?.slice(0,20)}`} className="text-sm text-purple-700">
                            {sug.icona} {sug.messaggio}
                          </p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Ultimi Trattamenti */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">ULTIMI TRATTAMENTI</p>
                  <div className="space-y-1">
                    {storico.ultimi_trattamenti?.slice(0, 5).map((app) => (
                      <div key={`tratt-${app.data}-${app.totale}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                        <div>
                          <span className="text-slate-500 text-xs">{formatData(app.data)}</span>
                          <p className="font-medium truncate max-w-[200px]">
                            {app.servizi?.map(s => s.nome).join(", ") || "Servizio"}
                          </p>
                        </div>
                        <span className="font-medium text-emerald-600">€{app.totale}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Tab Colore */}
              <TabsContent value="colore" className="p-4 space-y-2 mt-0">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="bg-pink-50 text-pink-700">
                    {storico.tempistiche.colore.totale_trattamenti} trattamenti
                  </Badge>
                  <span className="text-xs text-slate-500">Consigliato ogni 40 giorni</span>
                </div>
                {storico.storico_colore?.length > 0 ? (
                  storico.storico_colore.map((t) => (
                    <Card key={`colore-${t.data}-${t.servizio}`} className="border-l-4 border-l-pink-400">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{t.servizio}</p>
                            <p className="text-xs text-slate-500">{formatData(t.data)} • {t.operatore || "Operatore"}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{t.prezzo}</span>
                        </div>
                        {t.note && <p className="text-xs text-slate-400 mt-1">{t.note}</p>}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-center text-slate-400 py-8">Nessun trattamento colore</p>
                )}
              </TabsContent>

              {/* Tab Taglio */}
              <TabsContent value="taglio" className="p-4 space-y-2 mt-0">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {storico.tempistiche.taglio.totale_trattamenti} trattamenti
                  </Badge>
                  <span className="text-xs text-slate-500">Consigliato ogni 40 giorni</span>
                </div>
                {storico.storico_taglio?.length > 0 ? (
                  storico.storico_taglio.map((t) => (
                    <Card key={`taglio-${t.data}-${t.servizio}`} className="border-l-4 border-l-blue-400">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{t.servizio}</p>
                            <p className="text-xs text-slate-500">{formatData(t.data)} • {t.operatore || "Operatore"}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{t.prezzo}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-center text-slate-400 py-8">Nessun taglio registrato</p>
                )}
              </TabsContent>

              {/* Tab Schiaritura */}
              <TabsContent value="schiaritura" className="p-4 space-y-2 mt-0">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="bg-amber-50 text-amber-700">
                    {storico.tempistiche.schiaritura.totale_trattamenti} trattamenti
                  </Badge>
                  <span className="text-xs text-slate-500">Consigliato ogni 3 mesi</span>
                </div>
                {storico.storico_schiaritura?.length > 0 ? (
                  storico.storico_schiaritura.map((t) => (
                    <Card key={`schiaritura-${t.data}-${t.servizio}`} className="border-l-4 border-l-amber-400">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">{t.servizio}</p>
                            <p className="text-xs text-slate-500">{formatData(t.data)} • {t.operatore || "Operatore"}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{t.prezzo}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <p className="text-center text-slate-400 py-8">Nessuna schiaritura registrata</p>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-8 text-slate-400">
            Nessun dato disponibile
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
